// Stream consumption + terminal classification helpers for session-runner.ts.
// Split out to keep session-runner.ts under the 200-line budget.
import type { ChildProcess } from "node:child_process";
import type { BindingWriteResult } from "./autopilot-binding-file-writer.js";
import { classifyAttemptError } from "./error-classifier.js";
import { HookLifecycleEvidenceCollector } from "./hook-lifecycle-evidence-collector.js";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";
import { SecretRedactor } from "./secret-redactor.js";
import type { SessionRunResult, SessionSpec } from "./session-runner.js";
import { extractUsage } from "./session-runner-usage-extraction.js";
import { StderrRingBuffer } from "./stderr-ring-buffer.js";
import {
  type ParsedEvent,
  parseStreamJsonEvent,
  type SystemInitInventory,
} from "./stream-json-event-parser.js";
import { BoundedLineSplitter } from "./stream-json-line-splitter-bounded.js";
import { extractTerminalResult } from "./terminal-result-extractor.js";

export interface StreamState {
  events: ParsedEvent[];
  /** Parallel to `events`: clock time (ms) the bytes of that line arrived. */
  eventReceivedAtMs: number[];
  collector: HookLifecycleEvidenceCollector;
  sawInit: boolean;
  initInventory: SystemInitInventory | undefined;
  stderrRing: StderrRingBuffer;
}

// Redaction runs BEFORE any byte reaches a sink (review fix 3, phase spec
// Req 6 "Redaction trước mọi sink"): stdout raw JSON lines are redacted
// before JSON.parse (safe -- swapping a secret substring for [REDACTED]
// inside a JSON string value keeps the JSON structurally valid, since the
// placeholder introduces no unescaped quotes/backslashes), and stderr
// chunks are redacted before they enter the ring buffer.
export function attachStreamConsumers(
  child: ChildProcess,
  limits: RuntimeLimits,
  secretValues: string[] = [],
  now: () => number = Date.now,
): StreamState {
  const state: StreamState = {
    events: [],
    eventReceivedAtMs: [],
    collector: new HookLifecycleEvidenceCollector(),
    sawInit: false,
    initInventory: undefined,
    stderrRing: new StderrRingBuffer(limits.limits.stderrRingKib),
  };
  const splitter = new BoundedLineSplitter({
    lineMaxMib: limits.limits.lineMaxMib,
    rawLogMaxMib: limits.limits.rawLogMaxMib,
  });
  const stdoutRedactor = new SecretRedactor({ secretValues });
  const stderrRedactor = new SecretRedactor({ secretValues });

  // A line held back by the redactor/splitter is released by a LATER chunk
  // or by the exit flush; its bytes arrived no later than the latest chunk.
  let lastChunkAtMs = now();
  child.stdout?.on("data", (chunk: Buffer) => {
    lastChunkAtMs = now();
    const redactedChunk = stdoutRedactor.feed(chunk.toString("utf8"));
    for (const line of splitter.feed(Buffer.from(redactedChunk, "utf8"))) {
      const parsed = parseStreamJsonEvent(line);
      state.events.push(parsed);
      state.eventReceivedAtMs.push(lastChunkAtMs);
      if (parsed.kind === "system_init") {
        state.sawInit = true;
        state.initInventory = parsed.inventory;
      }
      state.collector.feed(parsed);
    }
  });
  child.stderr?.on("data", (chunk: Buffer) => {
    const redacted = stderrRedactor.feed(chunk.toString("utf8"));
    state.stderrRing.push(redacted);
  });
  child.once("exit", () => {
    // Flush each redactor's held-back tail (secret split across the very
    // last chunk boundary before EOF) into its respective sink.
    for (const line of splitter.feed(Buffer.from(stdoutRedactor.flush(), "utf8"))) {
      const parsed = parseStreamJsonEvent(line);
      state.events.push(parsed);
      state.eventReceivedAtMs.push(lastChunkAtMs);
      if (parsed.kind === "system_init") {
        state.sawInit = true;
        state.initInventory = parsed.inventory;
      }
      state.collector.feed(parsed);
    }
    state.stderrRing.push(stderrRedactor.flush());
  });

  return state;
}

/**
 * The raw `result` stream-json event, if the worker emitted exactly one —
 * independent of whether its `structured_output`/prose JSON parsed. A real
 * quota/rate-limit/auth failure has NO valid JSON body (§6.4's taxonomy is
 * about a plain-text provider message, e.g. "usage limit exceeded, resets
 * at..."), so gating classifier input on `extraction.kind === "ok"` would
 * mean every such failure gets miscategorized as a generic contract_error
 * instead of quota/rate_limit/auth_expired — the classifier's own priority
 * order (transport -> result.subtype/is_error -> rate-limit -> auth ->
 * permission -> task_failed) exists specifically to catch these BEFORE
 * falling back to task_failed, so it must see the raw text regardless of
 * schema validity.
 */
function rawResultEvent(
  events: ParsedEvent[],
): Extract<ParsedEvent, { kind: "result" }>["raw"] | undefined {
  const resultEvents = events.filter(
    (e): e is Extract<ParsedEvent, { kind: "result" }> => e.kind === "result",
  );
  return resultEvents.length === 1 ? resultEvents[0]?.raw : undefined;
}

export function finalizeResult(
  spec: SessionSpec,
  t0: number,
  spawnedAt: number,
  binding: BindingWriteResult,
  stream: StreamState,
  exitInfo: { code: number | null; signal: NodeJS.Signals | null },
  groupReaped: boolean,
): SessionRunResult {
  const extraction = extractTerminalResult(stream.events, {
    sessionId: spec.sessionId,
    runId: spec.runId,
  });
  const raw = rawResultEvent(stream.events);
  const classification = classifyAttemptError({
    exitCode: exitInfo.code,
    signal: exitInfo.signal,
    resultEvent:
      raw !== undefined
        ? { subtype: raw.subtype, is_error: raw.is_error, result: raw.result }
        : undefined,
    stderrTail: stream.stderrRing.read(),
    permissionDenials: raw?.permission_denials as never,
    hookDenyEvidence: stream.collector.all().some((o) => o.reasonCode !== undefined),
  });

  // quota/rate_limit never carry a valid structured_output (the provider
  // sends a plain-text usage message, not JSON — §5.3's schema is simply
  // absent), so `extraction.reason` would always read the uninformative
  // "no_result_json_found" for them. Surface the raw provider text instead
  // — quota-sleep-scheduler.ts's `computeWakeAt` parses THIS string for a
  // reset time before falling back to the injected backoff ladder (§6.4:
  // "parse reset có timezone hoặc backoff ladder").
  const isQuotaOrRateLimit =
    classification.category === "quota" || classification.category === "rate_limit";
  const quotaReason = isQuotaOrRateLimit ? (raw?.result ?? classification.reason) : undefined;

  return {
    outcome: extraction.kind === "contract_error" ? "contract_error" : "ok",
    reason: quotaReason ?? (extraction.kind === "contract_error" ? extraction.reason : undefined),
    category: classification.category,
    result: extraction.kind === "ok" ? extraction.structuredOutput : undefined,
    spawnAt: t0,
    spawnedAt,
    bindingPath: binding.bindingPath,
    bindingHash: binding.checksum,
    groupReaped,
    initInventory: stream.initInventory,
    rawResultTail: extraction.kind === "contract_error" ? extraction.rawResultTail : undefined,
    schemaErrorPaths:
      extraction.kind === "contract_error" ? extraction.schemaErrorPaths : undefined,
    usage: extractUsage(raw),
    // Phase 04 (F1b): only carried on a failed session (source (b) of the
    // contract-error breaker, Key Insight C1) — an "ok" outcome never needs
    // its stderr surfaced, and stream.stderrRing.read() is already the SAME
    // redacted text classifyAttemptError above just read.
    stderrTail: extraction.kind === "contract_error" ? stream.stderrRing.read() : undefined,
  };
}
